import React from 'react'
import styled from '@emotion/styled'
import {
  type Placement,
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom'
import {
  colors,
  font,
  layering,
  radius,
  shadow,
  spacing,
} from '@mobi/component-library/Foundations'
import { useRenderTimeoutControl } from '@mobi/utils/hooks/useRenderTimeoutControl'

const enum LocalConstants {
  AnimationTimingMs = 125,
}

type Props = {
  isOpen: boolean
  /** Popover will be placed relative to reference element */
  referenceElRef: React.RefObject<HTMLElement>
  /** Relative to reference, where should popover be placed */
  placement?: Placement
  /** Offset in pixels from reference on main axis */
  offsetYPx?: number
  /** Offset in pixels from reference on cross axis */
  offsetXPx?: number
  /** Allow auto placement with most space available */
  isAutoPlacement?: boolean
  children?: React.ReactNode
  /** Callback to dismiss menu on scroll */
  headingText?: string
  handleDismiss?: () => void
  /** Set to true to make the context menu full width */
  fullWidth?: boolean
  positionStrategy?: 'absolute' | 'fixed'
}

export const ContextMenu: React.FC<Props> = ({
  isOpen,
  referenceElRef,
  placement = 'bottom',
  offsetYPx = 0,
  offsetXPx = 0,
  isAutoPlacement = false,
  children,
  handleDismiss,
  headingText,
  fullWidth = false,
  positionStrategy = 'fixed',
}) => {
  const shouldRenderContextMenu = useRenderTimeoutControl({
    shouldRender: isOpen,
    timeoutMs: LocalConstants.AnimationTimingMs as number,
  })

  React.useEffect(() => {
    if (!isOpen || !handleDismiss) return
    const handleDismissOnScroll = () => handleDismiss()
    window.addEventListener('scroll', handleDismissOnScroll)
    return () => window.removeEventListener('scroll', handleDismissOnScroll)
  }, [isOpen, handleDismiss])

  if (!shouldRenderContextMenu) return null

  return (
    <ContextMenuComponent
      isOpen={isOpen}
      referenceElRef={referenceElRef}
      placement={placement}
      offsetYPx={offsetYPx}
      offsetXPx={offsetXPx}
      isAutoPlacement={isAutoPlacement}
      headingText={headingText}
      fullWidth={fullWidth}
      positionStrategy={positionStrategy}
    >
      {children}
    </ContextMenuComponent>
  )
}

// ===============
// Local Component
// ===============

const ContextMenuComponent: React.FC<Omit<Props, 'handleDismiss'>> = ({
  isOpen,
  referenceElRef,
  placement,
  offsetYPx = 0,
  offsetXPx = 0,
  isAutoPlacement,
  children,
  headingText,
  fullWidth,
  positionStrategy,
}) => {
  const middleware = React.useMemo(() => {
    const extras = [offset({ mainAxis: offsetYPx, crossAxis: offsetXPx }), shift()]
    if (isAutoPlacement) extras.push(autoPlacement())
    return extras
  }, [isAutoPlacement, offsetYPx, offsetXPx])

  const { floatingStyles, refs } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
    elements: { reference: referenceElRef.current },
    strategy: positionStrategy,
  })

  return (
    <ContextMenuStyled
      ref={refs.setFloating}
      data-testid='Overlay.ContextMenu'
      style={{ ...floatingStyles, width: fullWidth ? '100%' : undefined }}
    >
      <div className={isOpen ? 'context-menu--open' : 'context-menu--closed'}>
        {headingText && <span className='context-menu__heading'>{headingText}</span>}

        {children}
      </div>
    </ContextMenuStyled>
  )
}

// ======
// Styles
// ======

const ContextMenuStyled = styled.div({
  zIndex: layering.pagePopoverElements,

  '> div': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.smx1,
    padding: spacing.smx1,
    border: `0.1rem solid ${colors.neutral[200]}`,
    borderRadius: radius.lgx2,
    background: colors.white,
    boxShadow: shadow.lg,
    boxSizing: 'border-box',
    transformOrigin: 'top right',
    opacity: 0,
    scale: 0.93,
    transition: `opacity ${LocalConstants.AnimationTimingMs}ms cubic-bezier(0.34, 1.56, 0.64, 1), scale ${LocalConstants.AnimationTimingMs}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,

    '> .context-menu__heading': {
      padding: `0 ${spacing.smx2}`,
      color: colors.black,
      fontFamily: font.family.primary,
      fontSize: font.size.md.fontSize,
      letterSpacing: font.size.md.letterSpacing,
      lineHeight: font.size.md.lineHeight,
      fontWeight: font.weight.semibold,
      whiteSpace: 'nowrap',
    },

    '> button': {
      display: 'flex',
      justifyContent: 'flex-start',
      alignSelf: 'stretch',
    },

    '&.context-menu--open': {
      opacity: 1,
      scale: 1,
    },
  },
})
